import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { Suspense, lazy } from 'react'
import { Toaster } from './components/Toast'
import { CommandPalette } from './components/CommandPalette'

const Home = lazy(() => import('./pages/Home'))
const BookingPage = lazy(() => import('./pages/BookingPage'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const SignInPage = lazy(() => import('./pages/SignIn'))
const SignUpPage = lazy(() => import('./pages/SignUp'))
const IntakePage = lazy(() => import('./pages/IntakePage'))
const EstimatePage = lazy(() => import('./pages/EstimatePage'))
const CancelPage = lazy(() => import('./pages/CancelPage'))
const MyBookingsPage = lazy(() => import('./pages/MyBookingsPage'))
const InquiryPage = lazy(() => import('./pages/InquiryPage'))
const SuperAdminPage = lazy(() => import('./pages/SuperAdmin'))
const SubscribePage = lazy(() => import('./pages/SubscribePage'))

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth()
  if (!isLoaded) return <PageLoader />
  if (!isSignedIn) return <Navigate to="/sign-in" replace />
  return <>{children}</>
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-[#0A0A0A]">
      <div className="flex flex-col items-center gap-4">
        <img src="/logo-icon.png" alt="RinseOS" className="w-12 h-12 animate-pulse opacity-80" />
        <div className="w-32 h-1 rounded-full overflow-hidden bg-white/5">
          <div className="h-full bg-gradient-to-r from-[#006666] to-[#00EAFA] animate-[shimmer_1.5s_ease_infinite] bg-[length:200%]" />
        </div>
      </div>
    </div>
  )
}

const isCustomDomain =
  typeof window !== 'undefined' &&
  window.location.hostname !== 'app.rinseos.com' &&
  window.location.hostname !== 'rinseos.com' &&
  window.location.hostname !== 'www.rinseos.com' &&
  window.location.hostname !== 'localhost' &&
  !window.location.hostname.endsWith('.vercel.app')

export default function App() {
  if (isCustomDomain) {
    return (
      <Toaster>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="*" element={<BookingPage />} />
          </Routes>
        </Suspense>
      </Toaster>
    )
  }

  return (
    <Toaster>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/book/:slug" element={<BookingPage />} />
          <Route path="/intake/:token" element={<IntakePage />} />
          <Route path="/estimate/:token" element={<EstimatePage />} />
          <Route path="/cancel/:token" element={<CancelPage />} />
          <Route path="/my-bookings" element={<MyBookingsPage />} />
          <Route path="/inquiry/:slug" element={<InquiryPage />} />
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />
          <Route
            path="/subscribe"
            element={
              <ProtectedRoute>
                <SubscribePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute>
                <Dashboard />
                <CommandPalette />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/*"
            element={
              <ProtectedRoute>
                <SuperAdminPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Toaster>
  )
}
